/**
 * Image optimization utility functions
 * 
 * These functions help optimize images to improve LCP performance
 * and properly cache images from external sources like Supabase.
 */

/**
 * Creates an optimized image URL with width and quality parameters
 * 
 * @param url - Original image URL from Supabase or other source
 * @param width - Desired image width
 * @param quality - Image quality (1-100)
 * @returns Optimized image URL
 */
export function getOptimizedImageUrl(url: string, width: number = 1200, quality: number = 80): string {
  // Only optimize Supabase URLs
  if (!url || !url.includes('supabase.co')) {
    return url;
  }

  try {
    // Add cache-control parameters to URL
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}&quality=${quality}&cache=max-age=2592000`;
  } catch (error) {
    console.error('Error optimizing image URL:', error);
    return url;
  }
}

/**
 * Creates srcSet for responsive images
 * 
 * @param url - Original image URL
 * @param sizes - Array of image widths
 * @param quality - Image quality (1-100)
 * @returns srcSet string for responsive images
 */
export function createSrcSet(url: string, sizes: number[] = [640, 960, 1200, 1920], quality: number = 80): string {
  if (!url || !url.includes('supabase.co')) {
    return '';
  }

  try {
    return sizes
      .map(size => `${getOptimizedImageUrl(url, size, quality)} ${size}w`)
      .join(', ');
  } catch (error) {
    console.error('Error creating srcSet:', error);
    return '';
  }
}

/**
 * Gets placeholder color or blur data URL for an image
 * 
 * This is a simple implementation. For production, consider using
 * libraries like blurhash or a backend service to generate placeholders.
 * 
 * @param url - Original image URL
 * @returns Placeholder color or data URL
 */
export function getImagePlaceholder(url: string): string {
  // Simple placeholder based on URL hash
  if (!url) return '#e0e0e0';
  
  // Generate a simple color from the url string
  let hash = 0;
  for (let i = 0; i < url.length; i++) {
    hash = url.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate a dark neutral color for readability
  const h = Math.abs(hash) % 360;
  const s = 20 + Math.abs(hash) % 40; // 20-60%
  const l = 10 + Math.abs(hash) % 20; // 10-30%
  
  return `hsl(${h}, ${s}%, ${l}%)`;
} 